import { useEffect, useState, type ReactNode } from 'react';
import { Card } from '../components/ui/Card.tsx';
import { AuthApiError } from './data/authApi.ts';
import { useAuthSession } from './data/useAuthSession.ts';

type AuthGateProps = {
  children: (auth: {
    currentUserName: string | null;
    currentUserRole: string | null;
    authBypassed: boolean;
    onLogout: () => Promise<void>;
  }) => ReactNode;
};

function errorMessage(error: unknown): string {
  if (error instanceof AuthApiError) return error.message;
  return error instanceof Error ? error.message : 'Erreur inconnue.';
}

function roleLabel(role: string): string {
  switch (role) {
    case 'admin':
      return 'Administrateur';
    case 'dispatch':
      return 'Planification';
    case 'technician':
      return 'Technicien';
    case 'backoffice':
      return 'Exploitation';
    default:
      return 'Opérateur';
  }
}

function displayNameLabel(name: string): string {
  if (name === 'Dispatch FST') return 'Planification FST';
  if (name === 'Back-office FST') return 'Exploitation FST';
  return name;
}

export function AuthGate(props: AuthGateProps) {
  const { status, user, authBypassed, loginUsers, error, isSubmitting, login, logout } = useAuthSession();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!loginUsers.length) return;
    const activeUser = loginUsers.find((candidate) => candidate.id === selectedUserId) ?? loginUsers[0];
    if (!selectedUserId && !manualEmail) {
      setSelectedUserId(activeUser.id);
      setManualEmail(activeUser.email);
      return;
    }
    if (!manualEmail && activeUser.email) {
      setManualEmail(activeUser.email);
    }
  }, [loginUsers, manualEmail, selectedUserId]);

  if (status === 'authenticated' || status === 'bypassed') {
    return props.children({
      currentUserName: user?.displayName ?? null,
      currentUserRole: user?.role ?? null,
      authBypassed,
      onLogout: async () => {
        await logout();
      },
    });
  }

  if (status === 'loading') {
    return (
      <div className="auth-shell">
        <Card title="Initialisation de la session" subtitle="Vérification de l'accès opérateur en cours.">
          <div className="app-empty">
            <strong>Chargement</strong>
            <span>Lecture de la session locale et synchronisation avec l'API.</span>
          </div>
        </Card>
      </div>
    );
  }

  async function handleLogin() {
    const payload = selectedUserId
      ? { userId: selectedUserId, password }
      : manualEmail.trim()
        ? { email: manualEmail.trim(), password }
        : null;
    if (!payload) return;
    try {
      await login(payload);
    } catch {
      setPassword('');
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__header">
          <span className="material-symbols-outlined auth-card__icon">shield_lock</span>
          <h1>Pilotage Service Terrain</h1>
          <p>Connectez-vous pour accéder au cockpit opérationnel.</p>
        </div>

        {error ? (
          <div className="alert-card alert-card--danger">
            <strong>Connexion impossible</strong>
            <span>{errorMessage(error)}</span>
          </div>
        ) : null}

        {loginUsers.length ? (
          <div className="auth-user-grid">
            {loginUsers
              .filter((candidate) => candidate.active)
              .map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  className={`auth-user-card${candidate.id === selectedUserId ? ' is-active' : ''}`}
                  onClick={() => {
                    setSelectedUserId(candidate.id);
                    setManualEmail(candidate.email);
                    setPassword('');
                  }}
                >
                  <span className="auth-user-card__avatar">{candidate.displayName.slice(0, 2).toUpperCase()}</span>
                  <strong>{displayNameLabel(candidate.displayName)}</strong>
                  <span>{roleLabel(candidate.role)}</span>
                </button>
              ))}
          </div>
        ) : null}

        <div className="auth-card__fields">
          <label className="field-stack">
            <span>Email</span>
            <input
              type="email"
              value={manualEmail}
              onChange={(event) => {
                setManualEmail(event.target.value);
                if (event.target.value) setSelectedUserId('');
              }}
              placeholder="admin@field-service-technique.local"
            />
          </label>
          <label className="field-stack">
            <span>Mot de passe</span>
            <input
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              placeholder="Mot de passe opérateur"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleLogin();
                }
              }}
            />
          </label>
        </div>

        <div className="auth-card__hint">
          <span className="material-symbols-outlined">info</span>
          <span>Le mot de passe est requis pour activer la session. Le profil sélectionné renseigne automatiquement l'email.</span>
        </div>

        <button
          type="button"
          className="primary-button primary-button--block"
          onClick={() => void handleLogin()}
          disabled={isSubmitting || (!selectedUserId && !manualEmail.trim()) || !password}
        >
          <span className="material-symbols-outlined" style={{fontSize: '1.1rem'}}>login</span>
          Ouvrir la session
        </button>
      </div>
    </div>
  );
}
